import {Card, DataStatus, openConfirmDialog} from 'platform/components';
import {HStack, Text, VStack, Show} from 'platform/foundation';

import {useGetBusinessCaseQuery, useUnlinkTradeInMutation} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {useGetPrimaryAndSecondaryParameters} from '../../BusinessCaseHeader/hooks/useGetPrimaryAndSecondaryParameters';
import {useTradeInOffsetPrices} from '../hooks/useTradeInOffsetPrices';
import {BusinessCaseTradeInCounterpartCard} from './BusinessCaseTradeInCounterpartCard';

interface BusinessCaseTradeInLinkedProps {
  businessCaseId: string;
  linkedTradeInBusinessCaseId: string;
  isReadOnly: boolean;
}

export function BusinessCaseTradeInLinked(props: BusinessCaseTradeInLinkedProps) {
  const [unlinkTradeIn] = useUnlinkTradeInMutation();

  const {data: counterpart, isLoading: isLoadingCounterpart} = useGetBusinessCaseQuery({
    businessCaseId: props.linkedTradeInBusinessCaseId,
  });

  const {data: hostBusinessCase} = useGetBusinessCaseQuery({
    businessCaseId: props.businessCaseId,
  });

  const {primaryParameterAmount, primaryParameterCurrency} = useGetPrimaryAndSecondaryParameters(
    hostBusinessCase,
    undefined
  );
  const {formattedPrice, formattedOffsetSource} = useTradeInOffsetPrices({
    counterpart,
    baseAmount: primaryParameterAmount,
    baseCurrency: primaryParameterCurrency,
  });

  const handleUnlink = () => {
    openConfirmDialog({
      text: i18n.t('page.businessCase.tradeIn.unlinkConfirm'),
      onConfirm: () =>
        unlinkTradeIn({
          businessCaseId: props.businessCaseId,
          counterpartBusinessCaseId: props.linkedTradeInBusinessCaseId,
          xBranch: hostBusinessCase?.branchId,
        })
          .unwrap()
          .catch(handleApiError),
    });
  };

  return (
    <DataStatus isLoading={isLoadingCounterpart}>
      <VStack spacing={4}>
        <BusinessCaseTradeInCounterpartCard
          linkedTradeInBusinessCaseId={props.linkedTradeInBusinessCaseId}
          hostCustomerId={hostBusinessCase?.customerId}
          onUnlink={props.isReadOnly ? undefined : handleUnlink}
          data-testid={testIds.businessCase.detail('tradeInCounterpart')}
        />
        <Show when={hostBusinessCase?.businessCaseType === 'SELLING'}>
          <Card title={i18n.t('page.businessCase.tradeIn.offsetTitle')}>
            <VStack spacing={4}>
              <HStack spacing={2} justify="space-between">
                <Text size="small" color="secondary">
                  {i18n.t('page.businessCase.tradeIn.offsetValue')}:
                </Text>
                <Text size="small">{formattedPrice}</Text>
              </HStack>
              <HStack spacing={2} justify="space-between">
                <Text size="small" color="secondary">
                  {i18n.t('page.businessCase.tradeIn.offsetSourceBuying')}:
                </Text>
                <Text size="small">{formattedOffsetSource}</Text>
              </HStack>
            </VStack>
          </Card>
        </Show>
      </VStack>
    </DataStatus>
  );
}
